/**
 * "Call us, we'll find it" — floating concierge widget.
 *
 * Three channels (visibility controlled by /api/voice/config):
 *   1. Click-to-call (mobile only — `tel:` link to PatinaMart's AU number)
 *   2. "Request a callback" form — works without any Twilio integration
 *   3. "Talk to us right now" browser softphone via Twilio Voice JS SDK
 *      — only shown when TWILIO_* env vars are configured server-side.
 *
 * The widget is hidden entirely until `widget_enabled` is true on the server,
 * so we don't surface a "Call us" CTA while there's no human admin available.
 */
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Phone, X, Loader2, PhoneCall, MessageSquare, MicOff, PhoneOff } from 'lucide-react';
import { API, useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { devError } from '../utils/logger';
import { toast } from 'sonner';

export default function CallUsWidget() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Anonymous and plain-buyer accounts never see the widget — eligibility
    // is also enforced server-side, this is just to skip the fetch.
    if (!user) {
      setConfig({ widget_enabled: false });
      return;
    }
    axios
      .get(`${API}/voice/config`)
      .then((r) => setConfig(r.data))
      .catch(() => setConfig({ widget_enabled: false }));
  }, [user]);

  if (!config?.widget_enabled) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 px-4 py-3 rounded-full shadow-lg bg-accent text-accent-foreground hover:scale-105 transition-transform font-medium text-sm"
        data-testid="call-us-widget-trigger"
        aria-label="Call us, we'll find it"
      >
        <Phone className="w-4 h-4" />
        Call us — we'll find it
      </button>
      {open && <Panel config={config} onClose={() => setOpen(false)} />}
    </>
  );
}

function Panel({ config, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="call-us-panel"
    >
      <div
        className="bg-card border border-border/40 rounded-2xl w-full max-w-lg p-6 md:p-7 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-accent mb-1">Concierge sourcing</p>
            <h2 className="font-playfair text-2xl font-bold">Call us — we'll find it</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Tell us what you need. We'll work the supplier network and call you back.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {config.phone_display && (
          <a
            href={config.phone_tel_uri || `tel:${config.phone_display.replace(/[^0-9+]/g, '')}`}
            className="flex items-center gap-3 p-4 rounded-lg border border-accent/30 bg-accent/5 hover:bg-accent/10 transition-colors mb-4"
            data-testid="call-us-tel-link"
          >
            <Phone className="w-5 h-5 text-accent" />
            <div>
              <p className="text-xs text-muted-foreground">Call us directly</p>
              <p className="font-mono text-lg text-foreground">{config.phone_display}</p>
            </div>
          </a>
        )}

        {config.browser_call_enabled && <BrowserCall />}
        <CallbackForm onSent={onClose} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Callback request form — always available when widget enabled.
// ---------------------------------------------------------------------------
function CallbackForm({ onSent }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', what_you_need: '' });
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/voice/callback-requests`, {
        ...form,
        page_url: typeof window !== 'undefined' ? window.location.href : null,
      });
      toast.success("Got it — we'll call you within a few hours.");
      onSent?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="call-us-callback-form">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-1">
        <MessageSquare className="w-3.5 h-3.5" /> Or request a callback
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Input
          required value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="Your name *" maxLength={120}
          data-testid="call-us-name"
        />
        <Input
          required value={form.phone}
          onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
          placeholder="Your phone *" type="tel" maxLength={40}
          data-testid="call-us-phone"
        />
      </div>
      <Input
        value={form.email}
        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
        placeholder="Email (optional)" type="email" maxLength={160}
      />
      <Textarea
        required rows={3} minLength={5} maxLength={2000}
        value={form.what_you_need}
        onChange={(e) => setForm((f) => ({ ...f, what_you_need: e.target.value }))}
        placeholder="What are you looking for? E.g. 6m of 1900s baltic pine flooring, Adelaide pickup *"
        data-testid="call-us-message"
      />
      <Button type="submit" disabled={submitting} className="w-full" data-testid="call-us-submit">
        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Phone className="w-4 h-4 mr-2" />}
        Request a callback
      </Button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Twilio Voice JS SDK browser softphone.
// ---------------------------------------------------------------------------
function BrowserCall() {
  const [status, setStatus] = useState('idle');     // idle | connecting | active | ended | error
  const [error, setError] = useState(null);
  const [muted, setMuted] = useState(false);
  const deviceRef = useRef(null);
  const callRef = useRef(null);

  const start = async () => {
    setStatus('connecting');
    setError(null);
    try {
      const { data } = await axios.post(`${API}/voice/token`, { identity: null });
      // Lazy-load the SDK so it isn't bundled when the widget is disabled.
      const { Device } = await import('@twilio/voice-sdk');
      const device = new Device(data.token, { logLevel: 1 });
      deviceRef.current = device;

      device.on('error', (e) => {
        setStatus('error');
        setError(e.message || String(e));
      });
      device.on('disconnect', () => {
        setStatus('ended');
        callRef.current = null;
      });

      await device.register();
      // Outbound call — TwiML app on the backend bridges this to the admin's
      // browser identity and/or mobile number.
      const call = await device.connect({ params: { To: 'admin' } });
      callRef.current = call;
      call.on('accept', () => setStatus('active'));
      call.on('disconnect', () => {
        setStatus('ended');
        callRef.current = null;
      });
    } catch (e) {
      devError('voice start', e);
      setStatus('error');
      setError(e.response?.data?.detail || e.message || 'Could not start call');
    }
  };

  const hangup = () => {
    callRef.current?.disconnect();
    deviceRef.current?.destroy();
    setStatus('ended');
  };

  const toggleMute = () => {
    if (!callRef.current) return;
    const next = !muted;
    callRef.current.mute(next);
    setMuted(next);
  };

  // Auto-cleanup when the panel closes.
  useEffect(() => () => {
    callRef.current?.disconnect();
    deviceRef.current?.destroy();
  }, []);

  return (
    <div className="mb-5 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="browser-call-panel">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-emerald-300 mb-2">
        <PhoneCall className="w-3.5 h-3.5" /> Talk to us right now (browser only)
      </div>
      {status === 'idle' && (
        <Button
          type="button"
          onClick={start}
          className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
          data-testid="browser-call-start"
        >
          <PhoneCall className="w-4 h-4 mr-2" />
          Start call
        </Button>
      )}
      {status === 'connecting' && (
        <p className="text-sm text-emerald-200/80 inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Connecting…
        </p>
      )}
      {status === 'active' && (
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={toggleMute} data-testid="browser-call-mute">
            <MicOff className="w-4 h-4 mr-2" /> {muted ? 'Unmute' : 'Mute'}
          </Button>
          <Button type="button" onClick={hangup} className="bg-red-600 hover:bg-red-500 text-white flex-1" data-testid="browser-call-hangup">
            <PhoneOff className="w-4 h-4 mr-2" /> End call
          </Button>
        </div>
      )}
      {status === 'ended' && <p className="text-sm text-muted-foreground">Call ended.</p>}
      {status === 'error' && (
        <p className="text-sm text-red-400">Couldn't connect: {error}</p>
      )}
    </div>
  );
}
